// グローバルキーボードショートカット。
// - Ctrl + 「+」「-」: 段階ズーム（画面中央が中心）
// - Ctrl + C / X / V: 選択中オブジェクトのコピー / 切り取り / 貼り付け（貼り付けは use-paste-handler）
// - Ctrl + B: 選択中テキスト図形全体の太字切り替え
// - Ctrl + Z / Y: 自分の操作の Undo / Redo
// - Esc: ツール解除（idle へ戻る）
// - Delete / Backspace: 選択中オブジェクトの削除
// - Ctrl + ] / [: 選択中オブジェクトの前面 / 背面移動

import { useEffect } from "react";
import { useShowToast } from "../notice/use-show-toast";
import { useCanvasStore, type CanvasTool } from "../store/canvas-store";
import { useCanvasDocStore } from "../store/canvas-doc-store";
import { stepZoomIn, stepZoomOut, zoomAt } from "../../../lib/canvas-coordinates";
import { writeCanvasObjectsToClipboardData } from "../../../lib/canvas-clipboard";
import type { CanvasOpDispatcher } from "../use-canvas-op-dispatcher";
import { isTransientAttachmentObject } from "../attachment-placeholder";
import { resolveSelectionStyleTarget } from "../toolbar/selection-style-target";
import { isCharacterInputTarget, isEditingEventContext } from "./editing-target";
import { beginTableCellEdit } from "./use-table-cell-editor";

export function useGlobalShortcuts(
  dispatcher?: CanvasOpDispatcher | undefined,
  options?: { readonly readOnly?: boolean | undefined }
): void {
  const setView = useCanvasStore((s) => s.setView);
  const resetMode = useCanvasStore((s) => s.resetMode);
  const showToast = useShowToast();
  // 参照専用（埋め込み）では編集系ショートカットとコピー / 切り取りを一切登録・実行せず、
  // 中央基準のズーム（Ctrl + +/-）だけを残す。
  const readOnly = options?.readOnly ?? false;

  useEffect(() => {
    const onCopy = (event: ClipboardEvent): void => {
      // 本文編集の文脈（伝播経路上の編集要素、または現在のフォーカスが編集要素）では、
      // クリップボードも選択オブジェクトも読まずに即時 return し、preventDefault もしない。
      // 本文の選択文字列のコピーは、ローカル編集器またはブラウザ既定処理へ委ねる。
      if (isEditingEventContext(event) || !event.clipboardData) return;
      const selected = useCanvasStore.getState().selectedObjectIds;
      if (selected.length === 0) return;
      const objects = useCanvasDocStore.getState().objects;
      const copied = selected
        .map((id) => objects[id])
        .filter((o): o is NonNullable<typeof o> => Boolean(o));
      if (copied.length === 0) return;
      // アップロード未完了の画像・動画（blob:/data: src）は他参加者が解決できないため、
      // コピー対象から除外する（複製による壊れた画像や永続仮表示の再発を防ぐ）。
      // 混在選択では未完了添付だけを除外し、確定済みのオブジェクトは通常通りコピーする。
      const copyable = copied.filter((o) => !isTransientAttachmentObject(o));
      if (copyable.length === 0) {
        event.preventDefault();
        showToast(
          "warning",
          "アップロード完了前の画像・動画はコピーできません。完了後にもう一度お試しください。"
        );
        return;
      }
      writeCanvasObjectsToClipboardData(event.clipboardData, copyable);
      event.preventDefault();
      if (copyable.length < copied.length) {
        showToast("info", "アップロード完了前の画像・動画はコピー対象から除外しました。");
      }
    };

    // Ctrl+X: コピーと同じシリアライズを行ったうえで選択中オブジェクトを削除する。
    // 入力中のテキストフィールド側の「文字列の切り取り」操作を奪わないよう、編集対象のフォーカス時は何もしない。
    // ロックされているオブジェクトは削除対象から除外する（クリップボードへは含めて良い）。
    const onCut = (event: ClipboardEvent): void => {
      // 本文編集中は、クリップボードへの書き込み・preventDefault・図形削除・選択解除の
      // いずれも行わない（本文を編集しているだけで図形が消える事象を防ぐ）。
      if (isEditingEventContext(event) || !event.clipboardData) return;
      const selected = useCanvasStore.getState().selectedObjectIds;
      if (selected.length === 0) return;
      const objects = useCanvasDocStore.getState().objects;
      const copied = selected
        .map((id) => objects[id])
        .filter((o): o is NonNullable<typeof o> => Boolean(o));
      if (copied.length === 0) return;
      // コピーと同様に、アップロード未完了の画像・動画は切り取り（=複製＋削除）対象から除外する。
      // 未完了添付はクリップボードへ入れず、削除もせずその場に残す。
      const copyable = copied.filter((o) => !isTransientAttachmentObject(o));
      if (copyable.length === 0) {
        event.preventDefault();
        showToast(
          "warning",
          "アップロード完了前の画像・動画は切り取りできません。完了後にもう一度お試しください。"
        );
        return;
      }
      writeCanvasObjectsToClipboardData(event.clipboardData, copyable);
      event.preventDefault();
      if (dispatcher) {
        const copyableIds = new Set(copyable.map((o) => o.id));
        // 切り取り対象（=クリップボードへ入れたもの）のうち、ロックされていないものだけ削除する。
        const deletable = selected.filter((id) => copyableIds.has(id) && !objects[id]?.locked);
        if (deletable.length > 0) {
          dispatcher.deleteObjects(deletable);
        }
        useCanvasStore.getState().setSelectedObjectIds([]);
      }
      if (copyable.length < copied.length) {
        showToast("info", "アップロード完了前の画像・動画は切り取り対象から除外しました。");
      }
    };

    const onKeyDown = (event: KeyboardEvent): void => {
      // keydown 段階でも本文編集の所有を維持し、Ctrl / Meta 系のキャンバス操作と
      // 削除操作が編集中に走らないようにする（copy / cut / paste の実処理は native
      // イベント側が担うが、同じ根因による他の副作用も同時に防ぐ）。
      const editingTarget = isEditingEventContext(event);

      // 参照専用ではズーム（Ctrl + +/-）のみ許可し、編集系ショートカットは一切処理しない。
      if (readOnly) {
        if ((event.ctrlKey || event.metaKey) && !editingTarget) {
          if (event.key === "+" || event.key === ";" || event.key === "=") {
            event.preventDefault();
            const view = useCanvasStore.getState().view;
            const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
            setView(zoomAt(view, center, stepZoomIn(view.zoom)));
          } else if (event.key === "-" || event.key === "_") {
            event.preventDefault();
            const view = useCanvasStore.getState().view;
            const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
            setView(zoomAt(view, center, stepZoomOut(view.zoom)));
          }
        }
        return;
      }

      if (event.key === "Escape") {
        resetMode();
        return;
      }

      if ((event.key === "Delete" || event.key === "Backspace") && !editingTarget) {
        const selected = useCanvasStore.getState().selectedObjectIds;
        if (selected.length > 0 && dispatcher) {
          // 選択がある時点で「キャンバスへの削除ショートカット」として扱われるため、
          // 実際に削除対象が無い（全部ロック中など）場合でもブラウザ既定動作
          // （例: Backspace による履歴戻り）には流さない。
          event.preventDefault();
          // ロックされているオブジェクトは削除対象から除外する。
          const objects = useCanvasDocStore.getState().objects;
          const deletable = selected.filter((id) => !objects[id]?.locked);
          if (deletable.length > 0) {
            dispatcher.deleteObjects(deletable);
            useCanvasStore.getState().setSelectedObjectIds([]);
          }
        }
        return;
      }

      // 単一キーのツール切替 / グリッド吸着トグル（修飾キーなし・文字入力中でないときのみ）。
      // C=コメント, F=付箋, N=セクション, R=図形, T=テキスト, M=ペン, E=スタンプ, G=グリッド吸着切替。
      // キー長押しのオートリピートでは多重トグルしないよう event.repeat を除外する。
      // キャンバス面フォーカスのゲートは、初回表示直後や別 UI 操作後にキーが効かなくなるため撤去する。
      // Escape・Delete・Ctrl 系と同様にフォーカス位置には依存させず、文字キーを消費する対象のみ保護する。
      if (
        !isCharacterInputTarget(event.target) &&
        !event.isComposing &&
        !event.repeat &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.altKey &&
        !event.shiftKey
      ) {
        // F2: 単一選択中の付箋 / テキストを、ダブルクリックと同じ編集モードへ入れる
        // （Windows のリネーム操作等に倣ったキーボード操作）。編集開始は各オブジェクトの
        // autoEditObjectId effect（配置直後の自動編集と同じ経路）へ委ね、ロック中は effect 側で
        // 無視される。図形ラベル等の他種別は対象外（要望どおり付箋・テキストのみ）。
        // ツール切替と同じくフォーカス位置に依存せず、文字入力中と IME 変換中のみ抑止する。
        if (event.key === "F2") {
          const selected = useCanvasStore.getState().selectedObjectIds;
          const selectedId = selected.length === 1 ? selected[0] : undefined;
          if (selectedId) {
            const target = useCanvasDocStore.getState().objects[selectedId];
            if (target && (target.type === "sticky" || target.type === "text")) {
              event.preventDefault();
              useCanvasStore.getState().setAutoEditObjectId(target.id);
            } else if (target?.type === "table") {
              // 表は本文 1 本ではなくセルごとに本文を持つため、autoEditObjectId 経路とは分岐し、
              // 左上のセル（ヘッダ行の 1 列目）から専用の編集を開始する。
              event.preventDefault();
              beginTableCellEdit(target.id, 0, 0);
            }
          }
          return;
        }
        const singleKey = event.key.toLowerCase();
        const toolByKey: Partial<Record<string, CanvasTool>> = {
          c: "place-comment",
          f: "place-sticky",
          // セクションは、直感的な S が WASD パン（下移動）と衝突するため N を割り当てる。
          n: "place-section",
          r: "place-shape",
          t: "place-text",
          m: "draw-pen",
          l: "place-table",
          e: "place-stamp",
        };
        const mappedTool = toolByKey[singleKey];
        if (mappedTool !== undefined) {
          event.preventDefault();
          const { tool, setTool } = useCanvasStore.getState();
          // 同じツールキーの再押下で idle へ戻す（ツールバーボタンのトグルと同じ挙動）。
          setTool(tool === mappedTool ? "idle" : mappedTool);
          return;
        }
        if (singleKey === "g") {
          event.preventDefault();
          const { gridSnapEnabled, setGridSnapEnabled } = useCanvasStore.getState();
          setGridSnapEnabled(!gridSnapEnabled);
          return;
        }
        if (singleKey === "h") {
          // 選択操作の左右反転（キャンバス移動 / 範囲選択のボタン入れ替え）をトグルする。
          event.preventDefault();
          const { invertSelectPan, setInvertSelectPan } = useCanvasStore.getState();
          setInvertSelectPan(!invertSelectPan);
          return;
        }
      }

      if (!(event.ctrlKey || event.metaKey) || editingTarget) return;

      const key = event.key.toLowerCase();
      if (key === "b") {
        // 本文編集・参照専用は上の共通境界で除外済み。太字固有の修飾キーと IME を除外し、
        // キー押下時点の選択と文書から、選択後パネルと同じ適用対象・共通値を解決する。
        if (event.altKey || event.shiftKey || event.isComposing || !dispatcher) return;
        const { selectedObjectIds, tool } = useCanvasStore.getState();
        const target = resolveSelectionStyleTarget(
          useCanvasDocStore.getState().objects,
          selectedObjectIds,
          tool
        );
        if (target?.kind !== "text") return;
        // 有効対象の長押しは再反転させないが、ブラウザ既定の太字操作は継続して抑止する。
        event.preventDefault();
        if (!event.repeat) {
          dispatcher.updateObjectsStyle(target.targetIds, { bold: target.common.bold !== true });
        }
        return;
      }
      if (key === "z") {
        event.preventDefault();
        dispatcher?.undo();
        return;
      }
      if (key === "y") {
        event.preventDefault();
        dispatcher?.redo();
        return;
      }

      if (event.key === "]") {
        bringForward(dispatcher);
        event.preventDefault();
        return;
      }
      if (event.key === "[") {
        sendBackward(dispatcher);
        event.preventDefault();
        return;
      }

      if (event.key === "+" || event.key === ";" || event.key === "=") {
        // ; や = は Ctrl+= で来る環境がある。いずれもズームインに割り当てる。
        event.preventDefault();
        const view = useCanvasStore.getState().view;
        const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        setView(zoomAt(view, center, stepZoomIn(view.zoom)));
      } else if (event.key === "-" || event.key === "_") {
        event.preventDefault();
        const view = useCanvasStore.getState().view;
        const center = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
        setView(zoomAt(view, center, stepZoomOut(view.zoom)));
      }
    };
    window.addEventListener("keydown", onKeyDown);
    // 参照専用ではコピー / 切り取りは登録しない（クリップボード経由の複製も無効化する）。
    if (!readOnly) {
      window.addEventListener("copy", onCopy);
      window.addEventListener("cut", onCut);
    }
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("copy", onCopy);
      window.removeEventListener("cut", onCut);
    };
  }, [dispatcher, readOnly, resetMode, setView, showToast]);
}

function bringForward(dispatcher: CanvasOpDispatcher | undefined): void {
  if (!dispatcher) return;
  const selected = useCanvasStore.getState().selectedObjectIds;
  if (selected.length === 0) return;
  const objects = useCanvasDocStore.getState().objects;
  // ロック中オブジェクトは zOrder 変更の対象外。複数選択中も locked のものだけ除外する。
  const movable = selected.filter((id) => {
    const object = objects[id];
    return object !== undefined && !object.locked;
  });
  if (movable.length === 0) return;
  const maxZ = Math.max(0, ...Object.values(objects).map((o) => o.zOrder));
  // 1 ショートカット操作 = 1 履歴エントリにまとめ、Ctrl+Z 1 回でグループ全体を戻せるようにする。
  const updates = movable.map((id, index) => ({
    objectId: id,
    transform: { zOrder: maxZ + 1 + index },
  }));
  dispatcher.transformObjects(updates);
}

function sendBackward(dispatcher: CanvasOpDispatcher | undefined): void {
  if (!dispatcher) return;
  const selected = useCanvasStore.getState().selectedObjectIds;
  if (selected.length === 0) return;
  const objects = useCanvasDocStore.getState().objects;
  // ロック中オブジェクトは zOrder 変更の対象外。複数選択中も locked のものだけ除外する。
  const movable = selected.filter((id) => {
    const object = objects[id];
    return object !== undefined && !object.locked;
  });
  if (movable.length === 0) return;
  const minZ = Math.min(0, ...Object.values(objects).map((o) => o.zOrder));
  // 1 ショートカット操作 = 1 履歴エントリにまとめる（前面移動と同様）。
  const updates = movable.map((id, index) => ({
    objectId: id,
    transform: { zOrder: minZ - 1 - index },
  }));
  dispatcher.transformObjects(updates);
}
